import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Container,
  Typography,
  Grid,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Pagination,
} from "@mui/material";
import { Box } from "./MuiCompat";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ShareIcon from "@mui/icons-material/Share";
import { BlogPost, BlogApiResponse } from "../types/blog";
import { blogService } from "../services/blogService";

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const Blog: React.FC = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
  }>({ open: false, message: "" });
  const [headerHeight, setHeaderHeight] = useState(56);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const PAGE_SIZE = 8; // Assumed page size based on API response example

  // Fetch blog posts based on current page
  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        setLoading(true);
        const response: BlogApiResponse = await blogService.getAllPosts(
          currentPage
        );
        setBlogPosts(response.results);
        setTotalPages(Math.ceil(response.count / PAGE_SIZE));
        setError(null);
      } catch (err) {
        setError("Failed to load blog posts. Please try again later.");
        console.error("Error fetching blog posts:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPosts();
  }, [currentPage]);

  // Measure header height
  useEffect(() => {
    const updateHeaderHeight = () => {
      const appBar = document.querySelector("header.MuiAppBar-root");
      if (appBar) {
        const height = appBar.getBoundingClientRect().height;
        setHeaderHeight(height);
      }
    };

    updateHeaderHeight();
    window.addEventListener("resize", updateHeaderHeight);
    return () => window.removeEventListener("resize", updateHeaderHeight);
  }, []);

  const handleShare = (slug: string) => {
    const url = `${window.location.origin}/blog/${slug}`;
    navigator.clipboard.writeText(url).then(() => {
      setSnackbar({ open: true, message: "Link Copied to Clipboard" });
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
    window.scrollTo(0, 0); // Scroll to top on page change
  };

  // Sort blog posts by published date
  const sortedBlogPosts = [...blogPosts].sort((a, b) => {
    return new Date(b.published_date).getTime() - new Date(a.published_date).getTime();
  });

  if (loading) {
    return (
      <Box
        sx={{
          bgcolor: "primary.main",
          color: "#fff",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ bgcolor: "primary.main", color: "#fff", minHeight: "100vh" }}>
        <Box sx={{ height: headerHeight }} />
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
          <Typography
            variant="h4"
            sx={{ textAlign: "center", color: "error.main" }}
          >
            {error}
          </Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "primary.main", color: "#fff", minHeight: "100vh" }}>
      <Box sx={{ height: headerHeight }} />
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 }, pt: { xs: 4, md: 6 } }}>
        <Box sx={{ maxWidth: "xl", mx: "auto", mb: 8 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2.5rem", md: "4.5rem" },
              fontWeight: "bold",
              mb: 1,
            }}
          >
            Health & Longevity Insights
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2.5rem", md: "4.5rem" },
              fontWeight: "bold",
              mb: 2,
            }}
          >
            We Share
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontSize: "1.25rem", color: "grey.400", maxWidth: "32rem" }}
          >
            From cutting-edge science to real-world wisdom, we share insights to
            help you make smarter, more connected health choices.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {sortedBlogPosts.map((post) => (
            <Grid item xs={12} key={post.id}>
              <Link to={`/blog/${post.slug}`} style={{ textDecoration: "none" }}>
                <Grid container spacing={4} sx={{ alignItems: "flex-start" }}>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        overflow: "hidden",
                        borderRadius: "8px",
                        aspectRatio: "16/9",
                      }}
                    >
                      <img
                        src={post.thumbnail_url}
                        alt={post.title}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          transition: "transform 0.5s",
                        }}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.transform = "scale(1.05)")
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.transform = "scale(1)")
                        }
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                          flex: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ color: "grey.400" }}>
                          {formatDate(post.published_date)}
                        </Typography>
                        <Typography
                          variant="h3"
                          sx={{
                            fontSize: { xs: "1.5rem", md: "1.875rem" },
                            fontWeight: "bold",
                            lineHeight: "tight",
                            color: "white",
                            "&:hover": {
                              color: "pink.500",
                              textDecoration: "underline",
                            },
                            textDecoration: "none",
                            transition: "color 0.3s, text-decoration 0.3s",
                          }}
                        >
                          {post.title}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "grey.400" }}>
                          {post.excerpt}
                        </Typography>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            color: "white",
                            fontWeight: "medium",
                          }}
                        >
                          <Typography>Read Article</Typography>
                          <ArrowForwardIcon
                            sx={{ ml: 1, width: 16, height: 16 }}
                          />
                        </Box>
                      </Box>
                      <IconButton
                        onClick={(e) => {
                          e.preventDefault();
                          handleShare(post.slug);
                        }}
                        sx={{
                          color: "white",
                          "&:hover": { color: "pink.500" },
                          alignSelf: "flex-start",
                        }}
                        aria-label="Share blog post"
                      >
                        <ShareIcon />
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </Link>
            </Grid>
          ))}
        </Grid>

        {/* Pagination Component */}
        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "pink.500",
                    color: "white",
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: -4,
                      left: -4,
                      right: -4,
                      bottom: -4,
                      borderRadius: "50%",
                      backgroundColor: "rgba(255, 192, 203, 0.3)", // Translucent pink circle
                      zIndex: -1,
                    },
                  },
                },
              }}
            />
          </Box>
        )}
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Blog;